import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import IdleSessionWidget from './IdleSessionWidget';
import { Clock, Cloud, Sun, CloudRain, Snowflake, CloudLightning, CloudFog, Wind, Megaphone } from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

const WEATHER_MAP = {
  0: { label: 'Açık', Icon: Sun }, 1: { label: 'Az bulutlu', Icon: Sun },
  2: { label: 'Parçalı bulutlu', Icon: Cloud }, 3: { label: 'Bulutlu', Icon: Cloud },
  45: { label: 'Sisli', Icon: CloudFog }, 48: { label: 'Sisli', Icon: CloudFog },
  51: { label: 'Hafif çisenti', Icon: CloudRain }, 53: { label: 'Çisenti', Icon: CloudRain },
  55: { label: 'Yoğun çisenti', Icon: CloudRain }, 61: { label: 'Hafif yağmur', Icon: CloudRain },
  63: { label: 'Yağmur', Icon: CloudRain }, 65: { label: 'Şiddetli yağmur', Icon: CloudRain },
  71: { label: 'Hafif kar', Icon: Snowflake }, 73: { label: 'Kar', Icon: Snowflake },
  75: { label: 'Yoğun kar', Icon: Snowflake }, 80: { label: 'Sağanak', Icon: CloudRain },
  81: { label: 'Sağanak', Icon: CloudRain }, 82: { label: 'Şiddetli sağanak', Icon: CloudRain },
  95: { label: 'Gök gürültülü', Icon: CloudLightning }, 96: { label: 'Dolu', Icon: CloudLightning },
  99: { label: 'Şiddetli dolu', Icon: CloudLightning },
};

// — Bağımsız Saat (yalnızca kendisi re-render olur, üst bileşeni etkilemez)
const ClockBlock = memo(function ClockBlock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const timeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = now.toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  return (
    <div className="flex items-center gap-2 text-sm" data-testid="topbar-clock">
      <Clock className="h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
      <div className="min-w-0">
        <div className="font-bold text-slate-800 dark:text-slate-100 leading-tight text-sm sm:text-base font-mono">{timeStr}</div>
        <div className="text-[10px] text-slate-500 dark:text-slate-400 capitalize truncate hidden sm:block">{dateStr}</div>
      </div>
    </div>
  );
});

// — Bağımsız Marquee (yalnızca duyurular değişince güncellenir)
const AnnouncementMarquee = memo(function AnnouncementMarquee({ announcements }) {
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [idx, setIdx] = useState(0);

  // Aktif duyuruları filtrele
  const active = (announcements || []).filter(a => a && a.active !== false && (a.title || a.text));

  // Birden fazla duyuru varsa 8 saniyede bir döndür
  useEffect(() => {
    if (active.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % active.length), 8000);
    return () => clearInterval(t);
  }, [active.length]);

  const current = active[idx] || null;

  // Metin sığıyor mu? Sığmazsa animasyona geç.
  useLayoutEffect(() => {
    if (!containerRef.current || !innerRef.current) return;
    const cw = containerRef.current.clientWidth;
    const iw = innerRef.current.scrollWidth;
    setNeedsScroll(iw > cw + 8);
  }, [current]);

  if (!current) return null;

  const content = (
    <span className="inline-block">
      {current.title && <b className="mr-2 text-amber-900 dark:text-amber-200">{current.title}:</b>}
      <span className="text-amber-800 dark:text-amber-100">{current.text}</span>
    </span>
  );

  return (
    <div className="flex-1 min-w-0 mx-2 sm:mx-3 bg-gradient-to-r from-amber-50 via-rose-50 to-amber-50 dark:from-amber-900/30 dark:via-rose-900/30 dark:to-amber-900/30 border border-amber-200 dark:border-amber-700/50 rounded-lg px-3 py-2"
      data-testid="announcement-marquee">
      <div className="flex items-center gap-2">
        <Megaphone className="h-4 w-4 text-amber-600 dark:text-amber-300 flex-shrink-0 animate-pulse" />
        <div ref={containerRef} className="overflow-hidden flex-1 min-w-0">
          {needsScroll ? (
            <div ref={innerRef}
              className="whitespace-nowrap text-sm font-semibold inline-block"
              style={{ animation: 'marquee 22s linear infinite' }}>
              {content}<span className="inline-block w-20" />{content}
            </div>
          ) : (
            <div ref={innerRef} className="truncate text-sm font-semibold">
              {content}
            </div>
          )}
        </div>
        {active.length > 1 && (
          <div className="hidden md:flex items-center gap-1 flex-shrink-0">
            {active.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full transition ${i === idx ? 'bg-amber-600' : 'bg-amber-200'}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

// — Bağımsız hava durumu (sadece konum/hava güncellendiğinde değişir)
const WeatherBlock = memo(function WeatherBlock({ city, latitude, longitude }) {
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    if (!latitude || !longitude) { setWeather(null); return; }
    let active = true;
    const load = async () => {
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&timezone=auto`);
        const d = await r.json();
        if (active) setWeather({
          temp: Math.round(d?.current?.temperature_2m ?? 0),
          code: d?.current?.weather_code ?? 0,
        });
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 15 * 60 * 1000);
    return () => { active = false; clearInterval(t); };
  }, [latitude, longitude]);

  if (!city) return null;
  const W = weather ? (WEATHER_MAP[weather.code] || WEATHER_MAP[3]) : null;
  return (
    <div className="hidden sm:flex items-center gap-2 text-sm px-3 py-1.5 bg-sky-50 dark:bg-sky-900/30 border border-sky-200 dark:border-sky-700/50 rounded-lg flex-shrink-0"
      data-testid="weather-pill">
      {W ? <W.Icon className="h-4 w-4 text-sky-600 dark:text-sky-300" /> : <Wind className="h-4 w-4 text-sky-600 dark:text-sky-300" />}
      <div className="leading-tight">
        <div className="font-bold text-sky-800 dark:text-sky-100">{weather ? `${weather.temp}°C` : '—'}</div>
        <div className="text-[10px] text-sky-600 dark:text-sky-300 truncate max-w-[110px]">{city}{W ? ` · ${W.label}` : ''}</div>
      </div>
    </div>
  );
});

// — Oturum sayacı IdleSessionWidget'e taşındı (kendi state'i ile, parent re-render etmez)

export default function HesapTopbar() {
  const { user } = useHesapAuth();
  const token = useHesapAuth(s => s.token);
  const [announcements, setAnnouncements] = useState([]);
  const [siteCity, setSiteCity] = useState({ city: '', latitude: null, longitude: null });

  // /auth/me ile city/lat/lon hydrate et
  useEffect(() => {
    if (!token) return;
    let active = true;
    fetch(`${API}/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(me => {
        if (!active || !me) return;
        setSiteCity({ city: me.city || '', latitude: me.latitude, longitude: me.longitude });
        const stored = JSON.parse(localStorage.getItem('hesap_user') || 'null');
        if (stored && (stored.city !== me.city || stored.latitude !== me.latitude)) {
          const merged = { ...stored, city: me.city || '', latitude: me.latitude, longitude: me.longitude };
          localStorage.setItem('hesap_user', JSON.stringify(merged));
          useHesapAuth.setState({ user: merged });
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [token]);

  // localStorage'dan da hydrate (anında göster)
  useEffect(() => {
    if (user?.city) setSiteCity(c => c.city === user.city ? c : { city: user.city, latitude: user.latitude, longitude: user.longitude });
  }, [user?.city, user?.latitude, user?.longitude]);

  // Site settings (duyurular) — 2 dakikada bir tazele
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await fetch(`${API}/site-settings`);
        const d = await r.json();
        if (!active) return;
        const list = Array.isArray(d.announcements) ? d.announcements
                    : d.announcement_active && d.announcement ? [{ title: '', text: d.announcement, active: true }]
                    : [];
        setAnnouncements(list);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 2 * 60 * 1000);
    return () => { active = false; clearInterval(t); };
  }, []);

  return (
    <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-3 sm:px-4 lg:px-6 py-2 flex items-center gap-2 lg:gap-3"
      data-testid="hesap-topbar">
      <ClockBlock />
      <AnnouncementMarquee announcements={announcements} />
      <div className="flex items-center gap-2 flex-shrink-0">
        <WeatherBlock city={siteCity.city} latitude={siteCity.latitude} longitude={siteCity.longitude} />
        <IdleSessionWidget />
      </div>
    </div>
  );
}
